import { useCallback, useMemo, useState } from "react";
import {
  TbChevronDown,
  TbInfoCircle,
  TbSearch,
  TbShieldCheck,
} from "react-icons/tb";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { useToast } from "../../../../Hooks/toast";
import { formatCurrency } from "../../../../Utils/formatters";
import {
  CONTESTATION_REASONS,
  CONTESTATION_STATUS,
  getInitials,
  getReasonLabel,
  getStatusMeta,
  MED_INFO,
  MOCK_CONTESTATIONS,
  MOCK_PIX_TRANSACTIONS,
} from "./constants";

const LABEL_CLASS = "mb-2 block text-sm font-medium text-gray-500";
const INPUT_CLASS = "valut-input h-11 rounded-xl";
const TEXTAREA_CLASS = "valut-input rounded-xl py-3 resize-y";
const SELECT_CLASS = "valut-input h-11 w-full rounded-xl appearance-none !pr-10";

const TransactionOption = ({ transaction, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(transaction.id)}
    className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
      selected
        ? "border-[#e7a73f] bg-brand-cream/60"
        : "border-gray-200 bg-white hover:border-gray-300"
    }`}
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-dark text-xs font-bold text-white">
      {getInitials(transaction.name)}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-brand-dark">
        {transaction.name} — {transaction.bank}
      </p>
      <p className="truncate text-xs text-gray-500">{transaction.description}</p>
      <p className="text-[11px] text-gray-400">
        {formatCurrency(transaction.amountCents)} · {transaction.date} · ID {transaction.id}
      </p>
    </div>
  </button>
);

const ContestationRow = ({ item }) => {
  const status = getStatusMeta(item.status);

  return (
    <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-brand-dark">{item.id}</p>
          <span
            className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${status.className}`}
          >
            {status.label}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          {item.recipient} · {formatCurrency(item.amountCents)}
        </p>
        <p className="text-xs text-gray-400">
          Motivo: {getReasonLabel(item.reason)} · Aberta em {item.openedAt}
        </p>
        <p className="text-[11px] text-gray-400">Transação {item.transactionId}</p>
      </div>
    </div>
  );
};

export const PixContestacao = () => {
  const { addToast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransactionId, setSelectedTransactionId] = useState("");
  const [manualTransactionId, setManualTransactionId] = useState("");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [contestations, setContestations] = useState(MOCK_CONTESTATIONS);
  const [submitting, setSubmitting] = useState(false);

  const filteredTransactions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return MOCK_PIX_TRANSACTIONS;

    return MOCK_PIX_TRANSACTIONS.filter(
      (item) =>
        item.name.toLowerCase().includes(term) ||
        item.id.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term) ||
        item.bank.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  const resolvedTransactionId = selectedTransactionId || manualTransactionId.trim();

  const canSubmit =
    resolvedTransactionId.length > 0 &&
    reason.length > 0 &&
    description.trim().length >= 10 &&
    !submitting;

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!canSubmit) return;

      setSubmitting(true);

      const transaction =
        MOCK_PIX_TRANSACTIONS.find((item) => item.id === selectedTransactionId) ||
        null;

      const newContestation = {
        id: `MED-2025-${String(contestations.length + 43).padStart(4, "0")}`,
        transactionId: resolvedTransactionId,
        recipient: transaction?.name || "Destinatário informado",
        amountCents: transaction?.amountCents || 0,
        reason,
        openedAt: new Date().toLocaleDateString("pt-BR"),
        status: CONTESTATION_STATUS.em_analise.id,
      };

      setTimeout(() => {
        setContestations((prev) => [newContestation, ...prev]);
        setSelectedTransactionId("");
        setManualTransactionId("");
        setReason("");
        setDescription("");
        setSearchTerm("");
        setSubmitting(false);

        addToast({
          type: "success",
          title: "Contestação registrada",
          description:
            "Sua solicitação foi enviada para análise. Você será notificado sobre o andamento.",
        });
      }, 600);
    },
    [addToast, canSubmit, contestations.length, reason, resolvedTransactionId, selectedTransactionId]
  );

  return (
    <div className="w-full">
      <PageHeaderWithBack
        title="Contestação de Pix (MED)"
        subTitle="Solicite a devolução de valores por meio do Mecanismo Especial de Devolução do Banco Central."
        backTo="/pix"
      />

      <div className="mb-5 flex gap-3 rounded-xl border border-[#e7a73f]/20 bg-brand-cream/40 px-4 py-3">
        <TbInfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#e7a73f]" />
        <div className="text-xs leading-relaxed text-gray-600">
          <p className="font-semibold text-brand-dark">{MED_INFO.title}</p>
          <p className="mt-1">{MED_INFO.description}</p>
          <p className="mt-2">
            O prazo para contestação pode variar conforme o tipo de fraude. Mantenha comprovantes
            e registros da ocorrência para agilizar a análise.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="app-card mb-5 overflow-hidden">
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <TbShieldCheck className="h-4 w-4 text-[#e7a73f]" />
            <p className="text-sm font-bold text-brand-dark">Nova contestação</p>
          </div>
          <p className="text-xs text-gray-500">
            Selecione a transação Pix ou informe o identificador (EndToEnd) para abrir uma contestação.
          </p>
        </div>

        <div className="space-y-4 p-4">
          <div>
            <label htmlFor="med-search" className={LABEL_CLASS}>
              Buscar transação
            </label>
            <div className="relative">
              <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                id="med-search"
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Nome, banco, descrição ou ID da transação"
                className={`${INPUT_CLASS} !pl-10`}
              />
            </div>
          </div>

          <div className="space-y-2">
            <p className={LABEL_CLASS}>Transações recentes</p>
            {filteredTransactions.length > 0 ? (
              filteredTransactions.map((transaction) => (
                <TransactionOption
                  key={transaction.id}
                  transaction={transaction}
                  selected={selectedTransactionId === transaction.id}
                  onSelect={setSelectedTransactionId}
                />
              ))
            ) : (
              <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-xs text-gray-500">
                Nenhuma transação encontrada. Informe o ID EndToEnd abaixo.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="med-transaction-id" className={LABEL_CLASS}>
              ID da transação (EndToEnd)
            </label>
            <input
              id="med-transaction-id"
              type="text"
              value={manualTransactionId}
              onChange={(event) => {
                setManualTransactionId(event.target.value);
                if (event.target.value.trim()) {
                  setSelectedTransactionId("");
                }
              }}
              placeholder="Ex.: E2E20250617001"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="med-reason" className={LABEL_CLASS}>
              Motivo da contestação
            </label>
            <div className="relative">
              <select
                id="med-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                className={`${SELECT_CLASS} ${!reason ? "text-gray-400" : ""}`}
              >
                <option value="">Selecione o motivo</option>
                {CONTESTATION_REASONS.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
              <TbChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          <div>
            <label htmlFor="med-description" className={LABEL_CLASS}>
              Descrição detalhada
            </label>
            <textarea
              id="med-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
              placeholder="Descreva o ocorrido com o máximo de detalhes possível (mínimo 10 caracteres)."
              className={TEXTAREA_CLASS}
            />
            <p className="mt-1 text-[11px] text-gray-400">
              {description.trim().length}/10 caracteres mínimos
            </p>
          </div>

          <button type="submit" className="btn-primary w-full sm:w-auto" disabled={!canSubmit}>
            {submitting ? "Enviando..." : "Enviar contestação"}
          </button>
        </div>
      </form>

      <div className="app-card overflow-hidden">
        <div className="border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-bold text-brand-dark">Minhas contestações</p>
          <p className="text-xs text-gray-500">
            Acompanhe o status das solicitações abertas e finalizadas.
          </p>
        </div>

        {contestations.length > 0 ? (
          contestations.map((item) => <ContestationRow key={item.id} item={item} />)
        ) : (
          <p className="px-4 py-8 text-center text-sm text-gray-500">
            Você ainda não possui contestações registradas.
          </p>
        )}
      </div>
    </div>
  );
};
